// =============================================================================
// services/mutedWords.ts — per-device muted-words list (content filtering)
//
// A lightweight, client-side content filter: users add words/phrases they don't
// want to see, and the feed + comment threads hide any item whose text contains
// one. Stored locally in AsyncStorage (no account/migration needed) so it works
// immediately and privately on the device.
//
// Matching is case-insensitive and whole-word where possible (word boundaries),
// falling back to substring for multi-word phrases. Empty list = nothing hidden.
// =============================================================================
import AsyncStorage from '@react-native-async-storage/async-storage';

const KEY = 'cryb_muted_words';

// Load the stored muted words. Returns [] on any error or empty state.
export async function getMutedWords(): Promise<string[]> {
  try {
    const raw = await AsyncStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((w) => typeof w === 'string') : [];
  } catch {
    return [];
  }
}

// Persist the full list (deduped, trimmed, lowercased, non-empty).
export async function setMutedWords(words: string[]): Promise<void> {
  const clean = Array.from(
    new Set(words.map((w) => w.trim().toLowerCase()).filter((w) => w.length > 0))
  );
  try {
    await AsyncStorage.setItem(KEY, JSON.stringify(clean));
  } catch {
    /* non-fatal — filtering just won't persist */
  }
}

// Add one word/phrase; returns the updated list.
export async function addMutedWord(word: string): Promise<string[]> {
  const list = await getMutedWords();
  const next = [...list, word];
  await setMutedWords(next);
  return getMutedWords();
}

// Remove one word/phrase; returns the updated list.
export async function removeMutedWord(word: string): Promise<string[]> {
  const list = await getMutedWords();
  const next = list.filter((w) => w !== word.trim().toLowerCase());
  await setMutedWords(next);
  return next;
}

// True if `text` contains any muted word. Word-boundary match for single tokens,
// substring match for phrases. Used to hide posts/comments at render time.
export function containsMutedWord(text: string | undefined | null, words: string[]): boolean {
  if (!text || !words.length) return false;
  const haystack = text.toLowerCase();
  for (const w of words) {
    if (!w) continue;
    if (/\s/.test(w)) {
      // multi-word phrase → substring
      if (haystack.includes(w)) return true;
    } else {
      // single token → word-boundary (avoids matching "scunthorpe" for "cunt", etc.)
      const re = new RegExp(`(^|[^a-z0-9])${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`, 'i');
      if (re.test(haystack)) return true;
    }
  }
  return false;
}
